import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { SCENARIO_STEP } from 'commonTypes/api/scenarios/BaseTypes';
import type { OnlineGroupSection } from 'commonTypes/api/scenarios/CreditSelectionScreenContent';
import type { ContentOfType } from 'commonTypes/api/scenarios/scenario';

import { setScenario } from 'entities/scenario';

type TTimerData = {
  remaining: number;
};

export interface TimerState {
  timers: {
    [key in SCENARIO_STEP]?: TTimerData;
  };
}

const DEFAULT_TIMER_VALUE = 0;

const initialState: TimerState = {
  timers: {},
};

export const creditTimerSlice = createSlice({
  name: 'creditTimer',
  initialState,
  reducers: {
    tick(state, action: PayloadAction<SCENARIO_STEP>) {
      const timer = state.timers[action.payload];
      if (timer) {
        timer.remaining = Math.max(timer.remaining - 1, DEFAULT_TIMER_VALUE);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(setScenario, (state, { payload }) => {
      const { step } = payload;

      if (step === SCENARIO_STEP.CREDIT_SELECTION) {
        const onlineGroup = (payload.content as ContentOfType<SCENARIO_STEP.CREDIT_SELECTION>)?.sections?.find(
          (section) => section.type === 'onlineGroup',
        ) as OnlineGroupSection | undefined;

        state.timers[step] = {
          remaining: onlineGroup?.timerRemainingSeconds ?? DEFAULT_TIMER_VALUE,
        };
      }

      if (step === SCENARIO_STEP.OFFER_APPROVAL) {
        const content = payload.content as ContentOfType<SCENARIO_STEP.OFFER_APPROVAL>;
        state.timers[step] = {
          remaining: content.countdownBlock.timer.initialDuration,
        };
      }

      if (step === SCENARIO_STEP.OFFER_COOLING_PERIOD) {
        const content = payload.content as ContentOfType<SCENARIO_STEP.OFFER_COOLING_PERIOD>;
        state.timers[step] = {
          remaining: content.coolingPeriod.timer.initialDuration,
        };
      }

      if (step === SCENARIO_STEP.AGREEMENT_SIGNING) {
        const content = payload.content as ContentOfType<SCENARIO_STEP.AGREEMENT_SIGNING>;
        if (content.stepConfig.showConfirmCodeModal) {
          state.timers[step] = {
            remaining: content.confirmCodeModal.newCodeRequest.cooldown,
          };
        }
      }
    });
  },
});

export const { tick } = creditTimerSlice.actions;
